import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FlexLayoutModule } from '@angular/flex-layout';
import { IconModule } from '@visurel/iconify-angular';
import { ContainerModule } from 'src/@core/directives/container/container.module';
import { ColorFadeModule } from 'src/@core/pipes/color/color-fade.module';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MaterialShared } from './material-shared.module';
import { ConfirmDeleteDialogComponent } from './components/confirm-delete-dialog/confirm-delete-dialog.component';
import { ChangePasswordDialogComponent } from './components/change-password-dialog/change-password-dialog.component';
import { ColumnVisibilityDialogComponent } from './components/column-visibility-dialog/column-visibility-dialog.component';
import { IconComponent } from './components/icon/icon.component';
import { ParseDatePipe } from 'src/@core/pipes/parseDate.pipe';
import { NoDataFoundComponent } from './components/no-data-found/no-data-found.component';
import { ShowIfRoleDirective } from './directives/show-if-role.directive';
import { SearchAddressComponent } from './components/search-address/search-address.component';
import { AgmCoreModule } from '@agm/core';
import { AgmSnazzyInfoWindowModule } from '@agm/snazzy-info-window';
import { NgxMaterialTimepickerModule } from 'ngx-material-timepicker';
import { EmploymentTypePipe } from 'src/@core/pipes/employmentType.pipe';
import { ParseTimePipe } from 'src/@core/pipes/parseTime.pipe';
import { FormatDatePipe } from 'src/@core/pipes/formatDate.pipe';
import { AddEditEmploymentComponent } from '../modules/employment/employment/add-edit-employment/add-edit-employment.component';
import { EmployerPipe } from 'src/@core/pipes/data/employer.pipe';
import { CategoryPipe } from 'src/@core/pipes/data/category.pipe';
import { JobPipe } from 'src/@core/pipes/data/job.pipe';
import { JobTypePipe } from 'src/@core/pipes/data/jobType.pipe';
import { StatusPipe } from 'src/@core/pipes/status.pipe';
import { RatingComponent } from './components/rating/rating.component';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { NgImageSliderModule } from 'ng-image-slider';
import { JobSeekerPipe } from 'src/@core/pipes/data/jobseeker.pipe';
import { MomentModule } from 'ngx-moment';
import { DescriptionPipe } from 'src/@core/pipes/description.pipe';
import { DragDirective } from './directives/dragDrop.directive';
import { JobSeekerCardComponent } from '../modules/job-seeker/job-seeker-card/job-seeker-card.component';
import { PaymentStatusPipe } from 'src/@core/pipes/paymentStatus.pipe';
import { JobAdminShiftBurgerMenuComponent } from '../modules/job-admin/shift-burger-menu/shift-burger-menu.component';
import { UpdateEmployerDetailsComponent } from '../modules/dashboard/update-employer-details/update-employer-details.component';
import { WorkOrderBurgerMenuComponent } from '../modules/enterprise-admin/work-order/work-order-burger-menu/work-order-burger-menu.component';
import { MobileFormatPipe } from 'src/@core/pipes/mobile-format.pipe';
import { AddChampComponent } from '../modules/enterprise-admin/work-order/add-champ/add-champ.component';
import { WorkOrderTableComponent } from '../modules/enterprise-admin/work-order-table/work-order-table.component';
import { ViewActivationImageComponent } from '../modules/dashboard/view-activation-image/view-activation-image.component';
import { AddActivationImageComponent } from '../modules/dashboard/add-activation-image/add-activation-image.component';
import { CreateTeamMemberComponent } from '../modules/enterprise-admin/users/team-member/create-team-member/create-team-member.component';
import { CreateChampUserComponent } from '../modules/enterprise-admin/users/champ-user/create-champ-user/create-champ-user.component';
import { AddEditEmployerComponent } from '../modules/employer/add-edit-employer/add-edit-employer.component';
import { NgxDaterangepickerMd } from 'ngx-daterangepicker-material';
import { ShowIfPermissionDirective } from './directives/show-if-permission.directive';
import { LastSixDigitPipe } from '../last-six-digit.pipe';
import { EmploymentMapViewComponent } from '../modules/employment/active-employments/employment-map-view/employment-map-view.component';
import { AddVendorComponent } from '../modules/enterprise-admin/users/vendors/add-vendor/add-vendor.component';
import { FirstTimeLoginComponent } from '../modules/dashboard/first-time-login/first-time-login.component';
import { ShowIfEntityDirective } from './directives/show-if-entity.directive';
import { AddEditRolesComponent } from '../modules/roles-permissions/roles-list/add-edit-roles/add-edit-roles.component';
import { ViewMarketplaceComponent } from '../modules/marketplace/view-marketplace/view-marketplace.component';
import { DragDropUploadComponent } from '../drag-drop-upload/drag-drop-upload.component';
import { ViewProjectDetailsComponent } from '../modules/job/job/view-project/view-project-details/view-project-details.component';
import { DurationPipe } from 'src/@core/pipes/duration.pipe';
import { AuthServiceConfig, SocialLoginModule, GoogleLoginProvider } from 'angularx-social-login';
import { environment } from 'src/environments/environment';
import { AddCreditComponent } from '../modules/credits/add-credit/add-credit.component';
import { WithdrawComponent } from '../modules/credits/withdraw/withdraw.component';
import { NgOtpInputModule } from 'ng-otp-input';
import { CreditListComponent } from '../modules/credits/credit-list/credit-list.component';
import { UpgradeSubscriptionPopupComponent } from '../modules/credits/upgrade-subscription-popup/upgrade-subscription-popup.component';
import { TrialEndedPopupComponent } from '../modules/credits/trial-ended-popup/trial-ended-popup.component';
import { AudioPlayerComponent } from './components/audio-player/audio-player.component';
import { VocallabsSupportWidgetComponent } from './components/vocallabs-support-widget/vocallabs-support-widget.component';

const config = new AuthServiceConfig([
  {
    id: GoogleLoginProvider.PROVIDER_ID,
    provider: new GoogleLoginProvider(environment.googleClientId,
      { redirect_uri: 'http://localhost:4200/dashboard' }
    )
  }
]);

export function provideConfig() {
  return config;
}

export type CoraState = 'searching' | 'shortlisting' | 'finished' | 'parsing' | null;

@NgModule({
  declarations: [
    IconComponent,
    ConfirmDeleteDialogComponent,
    ChangePasswordDialogComponent,
    ColumnVisibilityDialogComponent,
    SearchAddressComponent,
    NoDataFoundComponent,
    AddEditEmploymentComponent,
    //pipe
    ParseDatePipe,
    EmploymentTypePipe,
    ParseTimePipe,
    FormatDatePipe,
    StatusPipe,
    PaymentStatusPipe,
    LastSixDigitPipe,

    //data pipe
    EmployerPipe,
    JobSeekerPipe,
    CategoryPipe,
    JobTypePipe,
    JobPipe,
    MobileFormatPipe,
    DurationPipe,
    //directive
    ShowIfRoleDirective,
    ShowIfEntityDirective,
    ShowIfPermissionDirective,
    DragDirective,
    RatingComponent,
    DescriptionPipe,
    JobSeekerCardComponent,
    JobAdminShiftBurgerMenuComponent,
    UpdateEmployerDetailsComponent,
    WorkOrderBurgerMenuComponent,
    AddChampComponent,
    WorkOrderTableComponent,
    ViewActivationImageComponent,
    AddActivationImageComponent,
    CreateTeamMemberComponent,
    CreateChampUserComponent,
    AddEditEmployerComponent,
    EmploymentMapViewComponent,
    AddVendorComponent,
    FirstTimeLoginComponent,
    AddEditRolesComponent,
    ViewMarketplaceComponent,
    DragDropUploadComponent,
    // CreditUsageListComponent,
    CreditListComponent,
    ViewProjectDetailsComponent,
    AddCreditComponent,
    WithdrawComponent,
    UpgradeSubscriptionPopupComponent,
    TrialEndedPopupComponent,
    AudioPlayerComponent,
    VocallabsSupportWidgetComponent
  ],
  imports: [
    CommonModule,
    MomentModule,
    FlexLayoutModule,
    IconModule,
    MaterialShared,
    ContainerModule,
    ColorFadeModule,
    FormsModule,
    ReactiveFormsModule,
    NgxMaterialTimepickerModule,
    AgmCoreModule.forRoot({
      apiKey: 'AIzaSyCapq8M5KTmGYayG3x7mLHSD2qXfzmyTC4',
      libraries: ['places', 'drawing', 'geometry']
    }),
    AgmSnazzyInfoWindowModule,
    NgxSkeletonLoaderModule,
    NgImageSliderModule,
    NgxDaterangepickerMd.forRoot(),
    SocialLoginModule,
    NgOtpInputModule
  ],
  exports: [
    CommonModule,
    MomentModule,
    FlexLayoutModule,
    IconModule,
    MaterialShared,
    ContainerModule,
    ColorFadeModule,
    FormsModule,
    ReactiveFormsModule,
    NgxMaterialTimepickerModule,
    NgxSkeletonLoaderModule,
    NgImageSliderModule,

    // components
    IconComponent,
    ConfirmDeleteDialogComponent,
    ChangePasswordDialogComponent,
    ColumnVisibilityDialogComponent,
    NoDataFoundComponent,
    SearchAddressComponent,
    AddEditEmploymentComponent,
    JobSeekerCardComponent,
    //directive
    ShowIfRoleDirective,
    ShowIfEntityDirective,
    ShowIfPermissionDirective,
    DragDirective,
    //pipe
    ParseDatePipe,
    EmploymentTypePipe,
    ParseTimePipe,
    FormatDatePipe,
    StatusPipe,
    PaymentStatusPipe,
    LastSixDigitPipe,
    DurationPipe,
    //data pipe
    EmployerPipe,
    JobSeekerPipe,
    CategoryPipe,
    JobTypePipe,
    JobPipe,
    MobileFormatPipe,
    RatingComponent,
    RatingComponent,
    DescriptionPipe,
    JobAdminShiftBurgerMenuComponent,
    WorkOrderBurgerMenuComponent,
    AddChampComponent,
    WorkOrderTableComponent,
    EmploymentMapViewComponent,
    ViewMarketplaceComponent,
    DragDropUploadComponent,
    // CreditUsageListComponent,
    CreditListComponent,
    ViewProjectDetailsComponent,
    AddCreditComponent,
    WithdrawComponent,
    AudioPlayerComponent,
    VocallabsSupportWidgetComponent
  ],
  entryComponents: [
    ConfirmDeleteDialogComponent,
    ChangePasswordDialogComponent,
    AddEditEmploymentComponent,
    ColumnVisibilityDialogComponent,
    UpdateEmployerDetailsComponent,
    AddChampComponent,
    ViewActivationImageComponent,
    AddActivationImageComponent,
    CreateTeamMemberComponent,
    CreateChampUserComponent,
    AddEditEmployerComponent,
    AddVendorComponent,
    FirstTimeLoginComponent,
    AddEditRolesComponent,
    ViewMarketplaceComponent,
    DragDropUploadComponent,
    // CreditUsageListComponent,
    CreditListComponent,
    ViewProjectDetailsComponent,
    AddCreditComponent,
    WithdrawComponent,
    UpgradeSubscriptionPopupComponent,
    TrialEndedPopupComponent
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [
    {
      provide: AuthServiceConfig,
      useFactory: provideConfig
    }
  ]
})

export class SharedModule { }


